import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useFirebase } from "../../../context/Firebase";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TextField from "@mui/material/TextField";
import Snackbar from "@mui/material/Snackbar";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import './UserDetailsPage.css';

const UserDetailsPage = () => {
  const { db } = useFirebase();
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [imageSrc, setImageSrc] = useState("");

useEffect(() => {
  const imageToTest = formData.picture?.trim();
  const gender = formData.gender;

  const img = new Image();

  if (imageToTest) {
    img.src = imageToTest;

    img.onload = () => setImageSrc(imageToTest);

    img.onerror = () => {
      const fallback =
        gender === "Male"
          ? "/default-male.jpg"
          : gender === "Other"
          ? "/default-other.png"
          : "/default-male.jpg";
      setImageSrc(fallback);
    };
  } else {
    const fallback =
      gender === "Female"
        ? "/default-female.jpg"
        : gender === "Other"
        ? "/default-other.png"
        : "/default-male.jpg";
    setImageSrc(fallback);
  }
}, [formData.picture, formData.gender]);


  useEffect(() => {
    if (!userId) return;

    const fetchUserData = async () => {
      setLoading(true);
      setError(null);
      try {
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          setFormData(data);
        } else {
          setError("User not found.");
        }
      } catch {
        setError("Failed to fetch user data.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [userId, db]);

  // Auto-enter edit mode if ?edit=true
  useEffect(() => {
    if (searchParams.get("edit") === "true" && userData && !editMode) {
      setEditMode(true);
      setFormData({ ...userData });
    }
  }, [searchParams, userData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleBlock = async () => {
    try {
      if (userData?.isAdmin) {
        setError("Admin users cannot be blocked.");
        return;
      }
      const newBlocked = !userData?.isBlocked;
      await updateDoc(doc(db, "users", userId), { isBlocked: newBlocked });
      setUserData((prev) => ({ ...prev, isBlocked: newBlocked }));
      setFormData((prev) => ({ ...prev, isBlocked: newBlocked }));
      setSuccess(newBlocked ? "User blocked." : "User unblocked.");
    } catch {
      setError("Failed to update block status.");
    }
  };

  const formatForInput = (dob) => {
    if (!dob) return "";
    if (dob.seconds) {
      const date = new Date(dob.seconds * 1000);
      return date.toISOString().split("T")[0];
    }
    return dob.split("T")[0];
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    }
    return timestamp;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validateForm = () => {
    const errors = {};
    if (formData.name?.trim() === "") errors.name = "Name is required";
    if (formData.email?.trim() === "") errors.email = "Email is required";
    if (formData.phone && !/^\d{10}$/.test(formData.phone))
      errors.phone = "Phone number must be exactly 10 digits";
    // Removed gender validation since it's now a free text field
    return errors;
  };

  const handleEditToggle = () => {
    if (!editMode) {
      setFormData({
        ...userData,
      });
    } else {
      setFormData(userData);
      setFormErrors({});
    }
    setEditMode(!editMode);
  };

  const handleStartChat = () => {
  navigate(`/chat/${userId}`, { state: { userName: formData.name } });
};


  const handleSave = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const updatedData = {
        ...formData,
        dob:
          typeof formData.dob === "string" && formData.dob !== ""
            ? Timestamp.fromDate(new Date(formData.dob))
            : formData.dob || null,
      };

      await updateDoc(doc(db, "users", userId), updatedData);
      setUserData(updatedData);
      setEditMode(false);
      setSuccess("User information updated successfully!");
    } catch (err) {
      console.error("Error updating user:", err);
      setError("Failed to update user information.");
    }
  };

  return (
    <div className="container">
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <>
          <Snackbar
            open={!!success}
            autoHideDuration={6000}
            message={success}
            onClose={() => setSuccess(null)}
          />
          <Snackbar
            open={!!error}
            autoHideDuration={6000}
            message={error}
            onClose={() => setError(null)}
          />

          <button
            onClick={() => window.history.back()}
            style={{
              padding: "6px 12px",
              marginBottom: "1rem",
              cursor: "pointer",
              borderRadius: "4px",
              border: "1px solid #ccc",
              backgroundColor: "#f0f0f0",
              fontSize: "1rem",
            }}
          >
            ← Back
          </button>

          <h1>User Details</h1>

          <div className="user-header">
<img
  src={imageSrc}
  alt={formData.name || "User"}
  className="user-avatar"
/>


            <div style={{ flex: 1 }}>
              {editMode ? (
                <TextField
                  label="User Name"
                  name="name"
                  value={formData.name || ""}
                  onChange={handleInputChange}
                  fullWidth
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />
              ) : (
                <h2 className="user-name">{userData?.name || "N/A"}</h2>
              )}
              {editMode ? (
                <TextField
                  label="Email"
                  name="email"
                  value={formData.email || ""}
                  onChange={handleInputChange}
                  fullWidth
                  sx={{ marginTop: '20px' }}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
              ) : (
                <p className="user-email">{userData?.email || "N/A"}</p>
              )}
            </div>
          </div>

          <div className="info-section">
            <h3 className="info-title">Personal Information</h3>
            <div className="info-content">
              {editMode ? (
                <>
                  <TextField
                    label="Bio"
                    name="bio"
                    value={formData.bio || ""}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={3}
                  />
                  <TextField
                    label="DOB"
                    name="dob"
                    type="date"
                    value={formData.dob ? formatForInput(formData.dob) : ""}
                    onChange={handleInputChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                  <FormControl fullWidth error={!!formErrors.gender}>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      name="gender"
                      value={formData.gender || ""}
                      onChange={handleInputChange}
                      label="Gender"
                    >
                      <MenuItem value="">
                        <em>Select Gender</em>
                      </MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Non-binary">Non-binary</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                      <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
                    </Select>
                    {formErrors.gender && (
                      <p style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '3px', marginLeft: '14px' }}>
                        {formErrors.gender}
                      </p>
                    )}
                  </FormControl>
                  <TextField
                    label="Phone"
                    name="phone"
                    value={formData.phone || ""}
                    onChange={handleInputChange}
                    fullWidth
                    error={!!formErrors.phone}
                    helperText={formErrors.phone}
                  />
                </>
              ) : (
                <>
                  <div className="info-item"><strong>Bio:</strong><br />{userData?.bio || "N/A"}</div>
                  <p className="info-item"><strong>DOB:</strong> {formatDate(userData?.dob)}</p>
                  <p className="info-item"><strong>Gender:</strong> {userData?.gender || "N/A"}</p>
                  <p className="info-item"><strong>Phone:</strong> {userData?.phone || "N/A"}</p>
                </>
              )}
              <p className="info-item"><strong>Blocked:</strong> {userData?.isBlocked ? <Chip label="Blocked" color="error" size="small" /> : <Chip label="Active" color="success" size="small" />}</p>
              <p className="info-item"><strong>Is Chatting:</strong> {userData?.isChatting ? "Yes" : "No"}</p>
            </div>
          </div>

          <div className="actions">
            {editMode ? (
              <>
                <Button variant="contained" color="primary" onClick={handleSave}>
                  Save
                </Button>
                <Button variant="outlined" onClick={handleEditToggle}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button variant="contained" onClick={handleEditToggle}>
                  Edit
                </Button>
                <Button
                  variant="contained"
                  color={userData?.isBlocked ? "success" : "error"}
                  startIcon={userData?.isBlocked ? <CheckCircleIcon /> : <BlockIcon />}
                  onClick={handleToggleBlock}
                >
                  {userData?.isBlocked ? "Unblock" : "Block"}
                </Button>
                <Button onClick={handleStartChat} variant="contained" endIcon={<SendIcon />}>
                  Chat with User
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default UserDetailsPage;
